<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useDialogStore } from "@/store/dialog";
import { useConfigStore } from "@/store/config";
import { useLoadingStore } from "@/store/loading";
import * as bootstrap from "bootstrap";

const router = useRouter();
const dialogStore = useDialogStore();
const configStore = useConfigStore();
const loadingStore = useLoadingStore();
const modalRef = ref(null);
let modalInstance = null;

let clickLockUntil = 0; // 本地鎖 - 使用時間戳

// 選擇線路並立即執行切換
const selectStream = async (item, index) => {
  // 立即檢查鎖定狀態
  const now = Date.now();
  if (now < clickLockUntil) {
    alert(
      dialogStore.isStreamMode
        ? `請勿頻繁切換線路,剩餘 ${clickLockUntil - now}ms`
        : `操作過於頻繁,剩餘 ${clickLockUntil - now}ms`,
    );
    return;
  }
  clickLockUntil = now + 3000; // 立即鎖定
  // 如果點擊當前已選中的線路,直接關閉
  if (configStore.currentStream === index + 1) {
    clickLockUntil = 0; // 解除鎖定
    await dialogStore.hideDialog();
    return;
  }
  try {
    // 執行回調,切換線路
    if (dialogStore.onConfirmCallback) {
      const selectedIndex = index + 1;
      await dialogStore.onConfirmCallback(item.ID, selectedIndex, item);
    }
  } catch (error) {
    console.error("切換線路失敗:", error);
  } finally {
    clickLockUntil = 0; // 解除鎖定
    await dialogStore.hideDialog();
  }
};

// 確認按鈕
const handleAction = async (action) => {
  const now = Date.now();
  if (now < clickLockUntil) {
    return;
  }
  clickLockUntil = now + 2000;
  try {
    switch (action) {
      case "logout":
        configStore.logout();
        dialogStore.hideDialog();
        await router.push("/login");
        break;
      case "alert":
        dialogStore.hideDialog();
        break;
      default:
        console.warn("Unknown action:", action);
        dialogStore.hideDialog();
    }
  } catch (error) {
    console.error("Action failed:", error);
  } finally {
    clickLockUntil = 0; // 解除鎖定
  }
};

// 防止長按
const preventLongPress = (e) => {
  e.preventDefault();
};
watch(
  () => dialogStore.action,
  (newVal) => {
    if (newVal === "") {
      dialogStore.hideDialog();
    }
  },
);
onMounted(() => {
  if (modalRef.value) {
    modalInstance = new bootstrap.Modal(modalRef.value, {
      backdrop: "static",
      keyboard: false,
    });

    dialogStore.$patch({
      _showModal: () => modalInstance?.show(),
      _hideModal: () => modalInstance?.hide(),
    });

    document.addEventListener("contextmenu", preventLongPress);
  }
});
onUnmounted(() => {
  document.removeEventListener("contextmenu", preventLongPress);
});
</script>

<template>
  <div
    id="alert-modal"
    class="modal fade"
    :class="dialogStore.action == 'stuck' ? 'bg-black' : ''"
    tabindex="-1"
    aria-labelledby="alertModalLabel"
    aria-hidden="true"
    ref="modalRef"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div
          class="modal-body d-flex flex-column justify-content-center align-items-center"
        >
          <!-- 一般文字內容 -->
          <p
            v-if="dialogStore.warnText"
            class="text-white text-center mb-0 mt-3"
            v-html="dialogStore.warnText"
          ></p>

          <!-- 線路選擇內容 -->
          <div v-if="dialogStore.isStreamMode" class="w-100">
            <div class="stream-container">
              <div
                class="btn-groups d-flex justify-content-center align-items-center flex-wrap gap-3"
              >
                <!-- @click="selectStream(item, index)" -->
                <button
                  v-for="(item, index) in dialogStore.options"
                  :key="item.ID"
                  type="button"
                  class="btn stream-btn text-white border-white btn-lg px-4 py-2"
                  :class="{
                    active: configStore.currentStream === index + 1,
                  }"
                  :disabled="dialogStore.isClosing"
                  @pointerup="selectStream(item, index)"
                >
                  <span class="stream-label">
                    {{ $t("config.playRoute") || "線路" }}
                  </span>
                  <span class="stream-number">
                    {{ configStore.getStreamNumber(index) }}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer justify-content-center border-0 py-1">
          <!-- 關閉按鈕 (線路選擇時顯示) -->
          <button
            v-if="dialogStore.isStreamMode"
            type="button"
            class="btn btn-danger btn-lg px-5"
            :disabled="dialogStore.isClosing"
            @click="dialogStore.hideDialog()"
          >
            {{ $t("config.close") }}
          </button>

          <!-- 確認按鈕 (只用於一般對話框) -->
          <button
            v-if="dialogStore.action !== 'stuck' && !dialogStore.isStreamMode"
            type="button"
            class="btn btn-success-dark btn-lg px-5"
            @click="handleAction(dialogStore.action)"
          >
            {{ $t("config.confirm") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.modal.show {
  overflow: hidden;
  height: 100dvh;
}
.modal-dialog {
  font-size: 24px;
  .btn {
    font-size: inherit;
    letter-spacing: 2px;
    transition: all 0.3s ease;
    &.active {
      background-color: rgba(51, 123, 83, 0.7);
      border: 1px solid rgba(51, 123, 83, 0.7) !important;
    }
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.modal-content {
  min-height: 400px;
  height: auto;
  background-color: #999999;
}

.stream-container {
  padding: 1rem;
}

@media (min-width: 576px) {
  .modal.show {
    height: 96dvh;
  }
  .modal-dialog {
    font-size: 16px;
  }
  .modal-content {
    min-height: 0;
    .modal-body {
      padding: 0;
    }
  }
}
@media (min-width: 992px) {
  .modal.show {
    height: 100dvh;
  }
  .modal-dialog {
    font-size: 24px;
  }
  .modal-content {
    min-height: 400px;
    .modal-body {
      padding: var(--bs-modal-padding);
    }
  }
}
</style>
